import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { fetchArticle, fetchArticles } from "@/lib/api";

export default function ArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    setArticle(null);
    setError(false);
    fetchArticle(slug)
      .then(setArticle)
      .catch(() => setError(true));
    fetchArticles().then((list) =>
      setRelated(list.filter((a) => a.slug !== slug).slice(0, 3))
    );
  }, [slug]);

  return (
    <div data-testid="article-page">
      <Navigation />
      <main className="pt-28 pb-24 min-h-screen">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            data-testid="article-back"
            onClick={() => navigate("/signal-library")}
            className="flex items-center gap-2 text-sm text-axiom-muted hover:text-axiom-text transition-colors mb-10"
          >
            <ArrowLeft size={14} /> Signal Library
          </button>

          {error && (
            <p
              data-testid="article-not-found"
              className="text-axiom-muted text-sm"
            >
              Transmission not found. The article may have been moved or
              retired.
            </p>
          )}

          {article && (
            <article>
              <div className="flex items-center gap-3 mb-6">
                <span className="num-label text-axiom-accent">
                  {article.eyebrow}
                </span>
                <div className="h-px flex-1 bg-white/10" />
                <span className="num-label">{article.read_time}</span>
              </div>

              <h1
                data-testid="article-title"
                className="font-display font-bold text-axiom-text tracking-tight text-3xl sm:text-5xl lg:text-[56px] leading-[1.05]"
              >
                {article.title}
              </h1>

              <p className="mt-6 text-axiom-muted text-base sm:text-lg leading-relaxed border-l-2 border-axiom-accent pl-4">
                {article.excerpt}
              </p>

              <div className="mt-12 space-y-6" data-testid="article-body">
                {article.body.map((p, i) => (
                  <p
                    key={i}
                    className="text-axiom-text/90 text-base sm:text-lg leading-[1.75]"
                  >
                    {p}
                  </p>
                ))}
              </div>

              <div className="mt-16 border-t border-white/10 pt-8">
                <div className="num-label mb-3">FILED UNDER</div>
                <div className="flex flex-wrap gap-2">
                  {["Operations", "Signal", "Cost Movement"].map((t) => (
                    <span
                      key={t}
                      className="text-xs text-axiom-muted px-3 py-1 border border-white/10"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </article>
          )}
        </div>

        {related.length > 0 && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24">
            <div className="flex items-center gap-3 mb-8">
              <span className="num-label">CONTINUE READING</span>
              <div className="h-px flex-1 bg-white/10" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
              {related.map((a) => (
                <button
                  key={a.slug}
                  data-testid={`related-${a.slug}`}
                  onClick={() => navigate(`/signal-library/${a.slug}`)}
                  className="bg-axiom-bg text-left p-6 hover:bg-axiom-surface transition-colors group"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="num-label text-axiom-accent">
                      {a.eyebrow}
                    </span>
                    <ArrowRight
                      size={14}
                      className="text-axiom-muted group-hover:text-axiom-accent transition-colors"
                    />
                  </div>
                  <h3 className="font-display text-base text-axiom-text font-bold leading-snug">
                    {a.title}
                  </h3>
                </button>
              ))}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
